import { forwardRef, memo, useRef, useCallback, useEffect, useLayoutEffect } from 'react';
import { Group, Rect, Circle, Text, Line, Arc, Path } from 'react-konva';
import { Node } from '../../../core/models/scene';
import Konva from 'konva';
import { useThemeAwareColor } from '../../../react-app/theme/ThemeProvider';
import { useTranslation } from '../../../hooks/useTranslation';
import { usePerspectiveStore } from '../../../stores/perspectiveStore';
import { useLabelSettingsStore } from '../../../stores/labelSettingsStore';
import type { ComputedCulture, ComputedHealth } from '../services/GenogramPerspectiveProjection';
import { resolveEffectiveShapeDimension } from '../../../core/shapes/effectiveDimensions';

interface PersonNodeProps {
  node: Node;
  selected: boolean;
  cultureMix?: ComputedCulture;
  healthMix?: ComputedHealth;
  onDragStart?: (pos: { x: number; y: number }) => void;
  onDragMove: (pos: { x: number; y: number }) => void;
  onDragEnd: (pos: { x: number; y: number }) => void;
  onClick: (e: any) => void;
  onDoubleClick?: (e: any) => void;
  onTransformStart?: (e: any) => void;
  onTransformEnd?: (e: any) => void;
  onTransform?: (e: any) => void;
  onStartTextEdit?: (field: 'name' | 'location', textNode: Konva.Text) => void;
  editingField?: 'name' | 'location' | null;
  canWrite?: boolean;
}

const PersonNodeInner = forwardRef<Konva.Group, PersonNodeProps>(({ 
  node, 
  selected, 
  cultureMix: cultureMixProp,
  healthMix: healthMixProp,
  onDragStart,
  onDragMove,
  onDragEnd,
  onClick,
  onDoubleClick: _onDoubleClick,
  onTransformStart,
  onTransformEnd,
  onTransform,
  onStartTextEdit,
  editingField,
  canWrite = true,
}, ref) => {
  const isPending = Boolean((node.attrs as any)?.isPendingAI);
  const width = resolveEffectiveShapeDimension(node, 'width') ?? 60;
  const height = resolveEffectiveShapeDimension(node, 'height') ?? 60;
  const size = Math.min(width, height);
  const snapToGrid = 12;
  const rotation = ((node.attrs as any)?.rotation as number) || 0;
  // derived sizes not currently used by this component

  const nameTextRef = useRef<Konva.Text>(null);
  const locationTextRef = useRef<Konva.Text>(null);
  const pendingBorderRef = useRef<Konva.Rect | null>(null);

  const getThemeColor = useThemeAwareColor();

  const getNodeColor = () => {
    let baseColor = '#f5f5f5'; // default

    if (node.fillColor) {
      baseColor = node.fillColor;
    } else if (node.location) {
      const colors: Record<string, string> = {
        'Italy': '#CD853F',
        'UK': '#4682B4', 
        'Vietnam': '#90EE90',
        'default': '#f5f5f5'
      };
      baseColor = colors[node.location] || colors.default;
    }
    
    // If cultural perspective is active, use dominant culture color for base fill
    if (perspective === 'cultural' && cultureMix.length > 0) {
      const dominant = [...cultureMix].sort((a, b) => b.weight - a.weight)[0];
      baseColor = dominant?.color || baseColor;
    }
    return getThemeColor(baseColor, { type: 'background' });
  };
  
  const getStrokeColor = () => {
    let baseColor = '#333';
    
    if (node.strokeColor) {
      baseColor = node.strokeColor;
    } else if (selected) {
      baseColor = '#2196f3';
    }
    
    return getThemeColor(baseColor, { type: 'foreground' });
  };

  const resolveSecondLineText = (key: string | null): string | null => {
    if (!key) return null;
    const anyNode = node as any;
    const props = (anyNode?.props ?? {}) as Record<string, unknown>;
    const attrs = (anyNode?.attrs ?? {}) as Record<string, unknown>;
    const raw = (() => {
      if (key === 'maritalStatus') return props[key] ?? attrs[key] ?? anyNode?.[key];
      if (key === 'gender') {
        return (
          (props as any).genderIdentity ??
          (attrs as any).genderIdentity ??
          props[key] ??
          attrs[key] ??
          anyNode?.[key]
        );
      }
      if (key === 'educationLevel') {
        return (
          props.educationLevel ??
          (attrs as any).educationLevel ??
          anyNode?.educationLevel ??
          props.education ??
          (attrs as any).education ??
          anyNode?.education
        );
      }
      if (key === 'participantId') {
        return (
          (props as any).researchId ??
          (attrs as any).researchId ??
          anyNode?.researchId ??
          props[key] ??
          attrs[key] ??
          anyNode?.[key]
        );
      }
      return anyNode?.[key] ?? props[key] ?? attrs[key];
    })();
    if (raw == null) return null;
    if (typeof raw === 'string') {
      const trimmed = raw.trim();
      return trimmed.length > 0 ? trimmed : null;
    }
    if (typeof raw === 'number') return String(raw);
    if (Array.isArray(raw)) return raw.length > 0 ? String(raw[0]) : null;
    return null;
  };

  const line2Field = useLabelSettingsStore((s) => s.line2Field);
  const { t } = useTranslation('genogram');
  const defaultPersonLabel = t('person.label', { defaultValue: 'Person' });
  const legacyEnPersonNames: Record<string, { k: string; def: string }> = {
    Person: { k: 'person.label', def: 'Person' },
    Partner: { k: 'relationships.partner', def: 'Partner' },
    Parent: { k: 'relationships.parent', def: 'Parent' },
    Child: { k: 'relationships.child', def: 'Child' },
    Sibling: { k: 'relationships.sibling', def: 'Sibling' },
    Male: { k: 'person.male', def: 'Male' },
    Female: { k: 'person.female', def: 'Female' },
    Unknown: { k: 'person.unknown', def: 'Unknown' },
  };
  const displayPersonName = (() => {
    const raw = node.name?.trim();
    if (!raw) return defaultPersonLabel;
    const legacy = raw ? legacyEnPersonNames[raw] : undefined;
    if (legacy) return t(legacy.k as any, { defaultValue: legacy.def });
    return node.name ?? defaultPersonLabel;
  })();
  const secondLine = resolveSecondLineText(line2Field);
  const secondLineTopY = (() => {
    if (!secondLine) return size + 20;
    const attrs = (node.attrs as any) || {};
    const nameFontSize = Number(attrs.fontSize) || 12;
    const rawLineHeight = Number(attrs.lineHeight);
    const lineHeightMultiplier =
      Number.isFinite(rawLineHeight) && rawLineHeight > 0
        ? Math.min(3, Math.max(0.5, rawLineHeight))
        : 1.2;

    const normaliseLineEndings = (value: string): string => value.replace(/\r\n?/g, '\n');
    const countLines = (value: unknown): number => {
      if (typeof value !== 'string') return 1;
      const normalized = normaliseLineEndings(value).replace(/\n+$/g, '');
      return Math.max(1, normalized.split('\n').length);
    };

    const estimateTextHeight = (lines: number, sizePx: number): number => {
      const lineHeightPx = sizePx * lineHeightMultiplier;
      const descentBuffer = Math.max(3, sizePx * 0.25);
      return sizePx + descentBuffer + Math.max(0, lines - 1) * lineHeightPx;
    };

    const nameLines = countLines(displayPersonName);
    const nameHeight = estimateTextHeight(nameLines, nameFontSize);
    const computed = size + 5 + nameHeight + 3;
    return Math.max(size + 20, computed);
  })();

  const deceasedOverlayColor = getThemeColor('#333333', { type: 'foreground' });
  
  const getTextStyles = () => {
    const attrs = (node.attrs as any) || {};
    const baseTextColor = attrs.textColor || '#333';
    const fw = (attrs.fontWeight || '').toString().toLowerCase();
    const fs = (attrs.fontStyle || '').toString().toLowerCase();
    const isBold = fw === 'bold' || fw === '700';
    const isItalic = fs === 'italic';
    const combinedStyle = isBold && isItalic ? 'bold italic' : isBold ? 'bold' : isItalic ? 'italic' : 'normal';
    return {
      fontSize: attrs.fontSize || 12,
      fontFamily: attrs.fontFamily || 'system-ui, -apple-system, sans-serif',
      // Keep fontWeight for compatibility, but Konva uses fontStyle; we combine here
      fontWeight: isBold ? 'bold' : 'normal',
      fontStyle: combinedStyle,
      textDecoration: attrs.textDecoration || 'none',
      textColor: getThemeColor(baseTextColor, { type: 'foreground' }),
      textAlign: attrs.textAlign || 'center',
      verticalAlign: attrs.verticalAlign || 'middle'
    };
  };
  
  const getStrokeStyles = () => {
    const attrs = (node.attrs as any) || {};
    const width = (attrs.strokeWidth !== undefined ? attrs.strokeWidth : (node as any).strokeWidth);
    return {
      strokeWidth: (typeof width === 'number' ? width : (selected ? 3 : 2)),
      dash: attrs.dash || [],
      dashEnabled: attrs.dashEnabled || false
    };
  };
  
  const handleNameDblClick = useCallback((e: any) => {
    e.cancelBubble = true;
    e.evt?.stopPropagation?.();
    e.evt?.preventDefault?.();
    if (onStartTextEdit && nameTextRef.current) {
      onStartTextEdit('name', nameTextRef.current);
    }
  }, [onStartTextEdit]);
  
  // Render square for male, circle for female
  const renderShape = () => {
    const isMale = node.sex === 'male';
    const isFemale = node.sex === 'female';
    const attrs = (node.attrs as any) || {};
    const isTransgender = attrs.genderIdentity === 'transgender';
    const isDeceased =
      typeof node.deceased === 'boolean'
        ? node.deceased
        : typeof attrs.deceased === 'boolean'
          ? attrs.deceased
          : typeof attrs.isDeceased === 'boolean'
            ? attrs.isDeceased
            : false;
    const isIndexPerson = attrs.isIndexPerson;
    const isPregnancy = attrs.pregnancy;
    const sexualOrientation = attrs.sexualOrientation;
    const isStillbirth = attrs.stillbirth;
    const shapeType = attrs.shapeType;

    // Pregnancy - triangle
    if (isPregnancy) {
      return (
        <Line
          points={[
            size/2, 0,
            size, size,
            0, size
          ]}
          closed
          fill={getNodeColor()}
          stroke={getStrokeColor()}
          strokeWidth={getStrokeStyles().strokeWidth}
          dash={getStrokeStyles().dash}
          dashEnabled={getStrokeStyles().dashEnabled}
        />
      );
    }

    if (attrs.culture || attrs.immigration || (typeof shapeType === 'string' && (shapeType.includes('culture') || shapeType.includes('immigration')))) {
      const { strokeWidth, dash, dashEnabled } = getStrokeStyles();
      const strokeCol = getStrokeColor();
      const fillCol = getNodeColor();

      const scaleFactor = 0.72;
      const sx = (size / 56) * scaleFactor;
      const sy = (size / 94) * scaleFactor;
      const rectSide = 54 * sx;
      const centerX = size / 2;
      const rectX = centerX - rectSide / 2;
      const rectY = 39 * sy;
      const stemX = centerX;
      const circleR = 27 * sx;
      const circleCx = centerX;
      const circleCy = rectY + circleR;
      const c = 28;
      const w = 0.9;
      const nx = (x: number) => centerX + ((c + (x - c) * w) * sx - 28 * sx);
      
      const imPath1 = `M ${nx(10.2224)} ${4.20515 * sy} C ${nx(9.20086)} ${12.064 * sy}, ${nx(14.9995)} ${23.187 * sy}, ${nx(27.0779)} ${20.7087 * sy} C ${nx(29.2112)} ${20.2711 * sy}, ${nx(33.1267)} ${17.7128 * sy}, ${nx(34.3581)} ${11.0182 * sy} C ${nx(34.8771)} ${8.19657 * sy}, ${nx(35.7481)} ${5.026 * sy}, ${nx(38.5067)} ${4.23786 * sy} C ${nx(41.2722)} ${3.44775 * sy}, ${nx(44.6243)} ${4.40579 * sy}, ${nx(46.9977)} ${10.4922 * sy}`;
      const imPath2 = `M ${nx(5.99563)} ${10 * sy} C ${nx(4.86028)} ${17.8306 * sy}, ${nx(13.576)} ${29.9694 * sy}, ${nx(27)} ${27.5 * sy} C ${nx(29.3155)} ${27.0741 * sy}, ${nx(34.8406)} ${25.6946 * sy}, ${nx(36.7972)} ${19.7586 * sy} C ${nx(37.7606)} ${16.836 * sy}, ${nx(38.7969)} ${13.3396 * sy}, ${nx(41.869)} ${13.1615 * sy} C ${nx(44.5277)} ${13.0073 * sy}, ${nx(47.1898)} ${14.5562 * sy}, ${nx(47.4988)} ${19.5 * sy}`;
      const isImmigration = attrs.immigration || (typeof shapeType === 'string' && shapeType.includes('immigration'));

      return (
        <>
          <Path
            data={`M ${stemX} ${40 * sy} V 0`}
            stroke={strokeCol}
            strokeWidth={strokeWidth}
            fillEnabled={false}
            strokeLinecap="round"
          />
          {isFemale ? (
            <Circle
              x={circleCx}
              y={circleCy}
              radius={circleR}
              fill={fillCol}
              stroke={strokeCol}
              strokeWidth={strokeWidth}
              dash={dash}
              dashEnabled={dashEnabled}
            />
          ) : (
            <Rect
              x={rectX}
              y={rectY}
              width={rectSide}
              height={rectSide}
              fill={fillCol}
              stroke={strokeCol}
              strokeWidth={strokeWidth}
              dash={dash}
              dashEnabled={dashEnabled}
            />
          )}
          <Path
            data={imPath1}
            stroke={strokeCol}
            strokeWidth={strokeWidth}
            fillEnabled={false}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          {isImmigration && (
            <Path
              data={imPath2}
              stroke={strokeCol}
              strokeWidth={strokeWidth}
              fillEnabled={false}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          )}
        </>
      );
    }

    // Institution - house shape
    if (shapeType === 'institution') {
      return (
        <Line
          points={[
            0, size,
            0, size/2,
            size/2, 0,
            size, size/2,
            size, size
          ]}
          closed
          fill={getNodeColor()}
          stroke={getStrokeColor()}
          strokeWidth={getStrokeStyles().strokeWidth}
          dash={getStrokeStyles().dash}
          dashEnabled={getStrokeStyles().dashEnabled}
        />
      );
    }

    // Pet - diamond
    if (shapeType === 'pet') {
      return (
        <Line
          points={[
            size/2, 0,
            size, size/2,
            size/2, size,
            0, size/2
          ]}
          closed
          fill={getNodeColor()}
          stroke={getStrokeColor()}
          strokeWidth={getStrokeStyles().strokeWidth}
          dash={getStrokeStyles().dash}
          dashEnabled={getStrokeStyles().dashEnabled}
        />
      );
    }

    // Transgender male - square with inner circle
    if (isMale && isTransgender) {
      return (
        <>
          <Rect
            x={0}
            y={0}
            width={size}
            height={size}
            fill={getNodeColor()}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
            dash={getStrokeStyles().dash}
            dashEnabled={getStrokeStyles().dashEnabled}
          />
          <Circle
            x={size/2}
            y={size/2}
            radius={size/2}Gender Based
            stroke={getStrokeColor()}
            fill={getNodeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
          />
        </>
      );
    }

    // Transgender female - circle with inner square
    if (isFemale && isTransgender) {
      return (
        <>
          <Circle
            x={size/2}
            y={size/2}
            radius={size/2}
            fill={getNodeColor()}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
            dash={getStrokeStyles().dash}
            dashEnabled={getStrokeStyles().dashEnabled}
          />
          <Rect
            x={size * 0.15}
            y={size * 0.15}
            width={size * 0.7}
            height={size * 0.7}
            fill=""
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
          />
        </>
      );
    }

    // Index person - reflect underlying sex and deceased status with double border
    if (isIndexPerson) {
      const strokeW = getStrokeStyles().strokeWidth;
      const strokeCol = getStrokeColor();
      const fillCol = getNodeColor();

      if (isMale) {
        return (
          <>
            <Rect
              x={0}
              y={0}
              width={size}
              height={size}
              fill={fillCol}
              stroke={strokeCol}
              strokeWidth={strokeW}
            />
            <Rect
              x={size * 0.15}
              y={size * 0.15}
              width={size * 0.7}
              height={size * 0.7}
              fillEnabled={false}
              stroke={strokeCol}
              strokeWidth={strokeW}
            />
            {isDeceased && (
              <>
                <Line points={[0, 0, size, size]} stroke={strokeCol} strokeWidth={2} />
                <Line points={[0, size, size, 0]} stroke={strokeCol} strokeWidth={2} />
              </>
            )}
          </>
        );
      }

      if (isFemale) {
        return (
          <>
            <Circle
              x={size/2}
              y={size/2}
              radius={size/2}
              fill={fillCol}
              stroke={strokeCol}
              strokeWidth={strokeW}
            />
            <Circle
              x={size/2}
              y={size/2}
              radius={size * 0.35}
              stroke={strokeCol}
              strokeWidth={strokeW}
            />
            {isDeceased && (
              <>
                <Line points={[0, 0, size, size]} stroke={strokeCol} strokeWidth={2} />
                <Line points={[0, size, size, 0]} stroke={strokeCol} strokeWidth={2} />
              </>
            )}
          </>
        );
      }

      // Unknown sex - diamond with inner diamond
      return (
        <>
          <Line
            points={[size/2, 0, size, size/2, size/2, size, 0, size/2]}
            closed
            fill={fillCol}
            stroke={strokeCol}
            strokeWidth={strokeW}
          />
          <Line
            points={[size/2, size * 0.15625, size * 0.84375, size/2, size/2, size * 0.84375, size * 0.15625, size/2]}
            closed
            fillEnabled={false}
            stroke={strokeCol}
            strokeWidth={strokeW}
          />
          {isDeceased && (
            <>
              <Line points={[size*0.1, size*0.1, size*0.9, size*0.9]} stroke={strokeCol} strokeWidth={2} />
              <Line points={[size*0.9, size*0.1, size*0.1, size*0.9]} stroke={strokeCol} strokeWidth={2} />
            </>
          )}
        </>
      );
    }

    // Gay - square with triangle indicator
    if (isMale && sexualOrientation === 'gay') {
      return (
        <>
          <Rect
            x={0}
            y={0}
            width={size}
            height={size}
            fill={getNodeColor()}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
          />
          <Line
            points={[
              size * 0.1, size * 0.1,       // top-left corner
              size * 0.9, size * 0.1,       // top-right corner
              size * 0.5, size * 0.9       // bottom point
            ]}
            closed
            fillEnabled={false}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
          />
        </>
      );
    }

    // Lesbian - circle with triangle indicator
    if (isFemale && sexualOrientation === 'lesbian') {
      return (
        <>
          <Circle
            x={size/2}
            y={size/2}
            radius={size/2}
            fill={getNodeColor()}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
            dash={getStrokeStyles().dash}
            dashEnabled={getStrokeStyles().dashEnabled}
          />
          <Line
            points={[
              size * 0.2, size * 0.2,       // top-left corner
              size * 0.8, size * 0.2,       // top-right corner
              size * 0.5, size * 0.9       // bottom point
            ]}
            closed
            fillEnabled={false}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
          />
        </>
      );
    }

    // Bisexual male - square with dashed arch
    if (isMale && sexualOrientation === 'bisexual') {
      return (
        <>
          <Rect
            x={0}
            y={0}
            width={size}
            height={size}
            fill={getNodeColor()}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
            dash={getStrokeStyles().dash}
            dashEnabled={getStrokeStyles().dashEnabled}
          />
          <Line
            points={[
              size * 0.1, size * 0.1,       // top-left corner
              size * 0.9, size * 0.1,       // top-right corner
              size * 0.5, size * 0.9       // bottom point
            ]}
            closed
            fillEnabled={false}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
            dash={[6, 3]}           // 6‑px stroke, 4‑px gap
            dashEnabled
          />
        </>
      );
    }

    // Bisexual female - circle with dashed arch
    if (isFemale && sexualOrientation === 'bisexual') {
      return (
        <>
          <Circle
            x={size/2}
            y={size/2}
            radius={size/2}
            fill={getNodeColor()}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
            dash={getStrokeStyles().dash}
            dashEnabled={getStrokeStyles().dashEnabled}
          />
          <Line
            points={[
              size * 0.2, size * 0.2,       // top-left corner
              size * 0.8, size * 0.2,       // top-right corner
              size * 0.5, size * 0.9       // bottom point
            ]}
            closed
            fillEnabled={false}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
            dash={[6, 3]} 
            dashEnabled={true}
          />
        </>
      );
    }

    if (shapeType === 'agender') {      const radius = size * 0.35;
      return (
        <>
          <Circle
            x={size / 2}
            y={size / 2}
            radius={radius}
            fill={getNodeColor()}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
          />
          <Line
            points={[size / 2, size / 2 - radius - size * 0.3, size / 2, size / 2 - radius * 1]}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
          />
          <Line
            points={[size / 2 - radius, size / 2, size / 2 + radius, size / 2]}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
          />
        </>
      );
    }

    if (shapeType === 'agender-classic') {      const radius = size * 0.35;
      const center = size / 2;
      return (
        <>
          <Circle
            x={center}
            y={center + radius * 0.3}
            radius={radius}
            fill={getNodeColor()}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
          />
          <Line
            points={[
              center,
              center - radius * 1.5,
              center,
              center - radius * 0.7
            ]}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
          />
        </>
      );
    }

    // Two-Spirit - stacked circles with opposing arms
    if (shapeType === 'two-spirit') {      return (
        <>
          <Circle
            x={size / 2}
            y={size * 0.3}
            radius={size * 0.22}
            fill={getNodeColor()}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
          />
          <Circle
            x={size / 2}
            y={size * 0.75}
            radius={size * 0.22}
            fill={getNodeColor()}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
          />
          <Line
            points={[-size * 0, size * 0.76, size * 0.3, size * 0.76]}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
          />
          <Line
            points={[size * 0.7, size * 0.3, size * 0.95, size * 0.3]}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
          />
        </>
      );
    }

    if (shapeType === 'epicene') {      const radius = size * 0.35;
      const center = size / 2;
      return (
        <>
          <Circle
            x={center}
            y={center}
            radius={radius}
            fill={getNodeColor()}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
          />
          <Circle
            x={center}
            y={center}
            radius={radius * 0.25}
            fill={getStrokeColor()}
            strokeEnabled={false}
          />
          <Line
            // Vertical equality bar
            points={[
              center,
              center - radius,
              center,
              center + radius
            ]}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
          />
          <Line
          points={[
            center - radius * 1.5,
            center,
            center - radius * 1,
            center
          ]}
          stroke={getStrokeColor()}
          strokeWidth={getStrokeStyles().strokeWidth}
        />
        <Line
        points={[
          center + radius * 1.5,
          center,
          center + radius * 1,
          center
        ]}
        stroke={getStrokeColor()}
        strokeWidth={getStrokeStyles().strokeWidth}
      /> 
      <Arc
      x={center + radius * 0}
      y={center}
      innerRadius={radius * 1.5}
      outerRadius={radius * 1.5}
      angle={-270}
      stroke={getStrokeColor()}
      strokeWidth={getStrokeStyles().strokeWidth}
    />
    <Arc
    x={center}
    y={center}
    innerRadius={radius * 1.5}
    outerRadius={radius * 1.5}
    angle={-270}
    rotation={180}
    stroke={getStrokeColor()}
    strokeWidth={getStrokeStyles().strokeWidth}
  />
  
        </>
      );
    }

    if (shapeType === 'epicene-classic') {      const radius = size * 0.35;
      const center = size / 2;
      return (
        <>
          <Circle
            x={center}
            y={center}
            radius={radius}
            fill={getNodeColor()}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
          />
          <Circle
            x={center}
            y={center}
            radius={radius * 0.35}
            fill={getStrokeColor()}
            strokeEnabled={false}
          />
          <Line
          points={[
            center + radius,
            center - radius * 0.2,
            center + radius * 1.3,
            center - radius * 0.7
          ]}
          stroke={getStrokeColor()}
          strokeWidth={getStrokeStyles().strokeWidth}
        />
        
        <Line
          points={[
            center - radius,
            center + radius * 0.2,
            center - radius * 1.3,
            center + radius * 0.7
          ]}
          stroke={getStrokeColor()}
          strokeWidth={getStrokeStyles().strokeWidth}
        />
        </>
      );
    }

    if (shapeType === 'gender-fluid') {      
      const radius = size * 0.35;
      const center = size / 2;
      return (
        <>
          <Circle
            x={center}
            y={center}
            radius={radius}
            fill={getNodeColor()}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
          />
          <Line
          // Diagonal stem running northwest from circle
          points={[
            center - radius * 0.70,
            center - radius * 0.70,
            center - radius * 1.38,
            center - radius * 1.38
          ]}
          stroke={getStrokeColor()}
          strokeWidth={getStrokeStyles().strokeWidth}
        />
        <Line
          // Arrowhead leg on the north side of the stem tip
          points={[
            center - radius * 1.38,
            center - radius * 1.38,
            center - radius * 1.38 + radius * 0.35 * Math.SQRT1_2 + radius * 0.38 * 0.80 * Math.SQRT1_2,
            center - radius * 1.38 + radius * 0.35 * Math.SQRT1_2 - radius * 0.38 * 0.80 * Math.SQRT1_2
          ]}
          stroke={getStrokeColor()}
          strokeWidth={getStrokeStyles().strokeWidth}
        />
        <Line
          // Arrowhead leg on the west side of the stem tip
          points={[
            center - radius * 1.38,
            center - radius * 1.38,
            center - radius * 1.38 + radius * 0.35 * Math.SQRT1_2 - radius * 0.38 * 0.80 * Math.SQRT1_2,
            center - radius * 1.38 + radius * 0.35 * Math.SQRT1_2 + radius * 0.38 * 0.80 * Math.SQRT1_2
          ]}
          stroke={getStrokeColor()}
          strokeWidth={getStrokeStyles().strokeWidth}
        />
        <Line
        points={[
          center + radius * 1 - radius * 0.3,
          center + radius * 1 - radius * 0.3,
          center + radius * 1 + radius * 0.3,
          center + radius * 1 + radius * 0.3
        ]}
        stroke={getStrokeColor()}
        strokeWidth={getStrokeStyles().strokeWidth}
      />
      <Line
        points={[
          center + radius * 1 - radius * 0.3,
          center + radius * 1 + radius * 0.3,
          center + radius * 1 + radius * 0.3,
          center + radius * 1 - radius * 0.3
        ]}
        stroke={getStrokeColor()}
        strokeWidth={getStrokeStyles().strokeWidth}
      />
      <Line
      points={[
        center + radius,
        center - radius * 0.2,
        center + radius * 1.3,
        center - radius * 0.7
      ]}
      stroke={getStrokeColor()}
      strokeWidth={getStrokeStyles().strokeWidth}
    />
    
    <Line
      points={[
        center - radius,
        center + radius * 0.2,
        center - radius * 1.3,
        center + radius * 0.7
      ]}
      stroke={getStrokeColor()}
      strokeWidth={getStrokeStyles().strokeWidth}
    />
        </>
      );
    }

    if (shapeType === 'gender-fluid-classic') {      const radius = size * 0.35;
      const center = size / 2;
      const circleRadius = radius * 0.75;
      const circleCenterY = center - radius * 0.4;
      const arrowTipX = center + radius * 0.1;
      const arrowTipY = center + radius * 1.24;
      const arrowWing = radius * 0.3;
      return (
        <>
          <Circle
            x={center}
            y={circleCenterY}
            radius={circleRadius}
            fill={getNodeColor()}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
          />
          <Path
          data={`
            M ${center + radius * 0.24} ${center + radius * 0.72}
            C ${center + radius * 0.54} ${center + radius * 0.92},
              ${center + radius * 0.36} ${center + radius * 1.15},
              ${center + radius * 0.12} ${center + radius * 1.18}
          `}
          stroke={getStrokeColor()}
          strokeWidth={getStrokeStyles().strokeWidth}
          fillEnabled={false}
        />
        <Path
          data={`
            M ${center} ${center + radius * 0.35}
            C ${center - radius * 0.24} ${center + radius * 0.48},
              ${center - radius * 0.06} ${center + radius * 0.78},
              ${center + radius * 0.24} ${center + radius * 0.74}
          `}
          stroke={getStrokeColor()}
          strokeWidth={getStrokeStyles().strokeWidth}
          fillEnabled={false}
        />
        <Line
          points={[
            arrowTipX,
            arrowTipY,
            arrowTipX + arrowWing,
            arrowTipY
          ]}
          stroke={getStrokeColor()}
          strokeWidth={getStrokeStyles().strokeWidth}
        />
        <Line
          points={[
            arrowTipX,
            arrowTipY,
            arrowTipX,
            arrowTipY - arrowWing
          ]}
          stroke={getStrokeColor()}
          strokeWidth={getStrokeStyles().strokeWidth}
        />
      
        </>
      );
    }

    if (shapeType === 'bigender') {      const radius = size * 0.35;
      const center = size / 2;
      return (
        <>
          <Circle
            x={center}
            y={center}
            radius={radius}
            fill={getNodeColor()}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
          />
          <Line
          points={[
            center + radius * 0.7,
            center - radius * 0.7,
            center + radius * 1.4,
            center - radius * 1.4
          ]}
          stroke={getStrokeColor()}
          strokeWidth={getStrokeStyles().strokeWidth}
          />
          <Line
          points={[
            center + radius * 1.4,
            center - radius * 1.4,
            center + radius * 1.0,
            center - radius * 1.4
          ]}
          stroke={getStrokeColor()}
          strokeWidth={getStrokeStyles().strokeWidth}
          />
          <Line
          points={[
            center + radius * 1.4,
            center - radius * 1.4,
            center + radius * 1.4,
            center - radius * 1.0
          ]}
          stroke={getStrokeColor()}
          strokeWidth={getStrokeStyles().strokeWidth}
          />
          <Line
          points={[
            center,
            center + radius * 1,
            center,
            center + radius * 1.6
          ]}
          stroke={getStrokeColor()}
          strokeWidth={getStrokeStyles().strokeWidth}
        />
        <Line
          points={[
            center - radius * 0.4,
            center + radius * 1.3,
            center + radius * 0.4,
            center + radius * 1.3
          ]}
          stroke={getStrokeColor()}
          strokeWidth={getStrokeStyles().strokeWidth}
        />
        </>
      );
    }

    if (shapeType === 'trigender') {      const radius = size * 0.35;
      const center = size / 2;
      return (
        <>
          <Circle
            x={center}
            y={center}
            radius={radius}
            fill={getNodeColor()}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
          />
          <Line
          points={[
            center + radius * 0.7,
            center - radius * 0.7,
            center + radius * 1.4,
            center - radius * 1.4
          ]}
          stroke={getStrokeColor()}
          strokeWidth={getStrokeStyles().strokeWidth}
          />
          <Line
          points={[
            center + radius * 1.4,
            center - radius * 1.4,
            center + radius * 1.0,
            center - radius * 1.4
          ]}
          stroke={getStrokeColor()}
          strokeWidth={getStrokeStyles().strokeWidth}
          />
          <Line
          points={[
            center + radius * 1.4,
            center - radius * 1.4,
            center + radius * 1.4,
            center - radius * 1.0
          ]}
          stroke={getStrokeColor()}
          strokeWidth={getStrokeStyles().strokeWidth}
          />
          <Line
          // Star horizontal arm
          points={[
            center - radius * 1.2 - radius * 0.35,
            center - radius * 1.2,
            center - radius * 1.2 + radius * 0.35,
            center - radius * 1.2
          ]}
          stroke={getStrokeColor()}
          strokeWidth={getStrokeStyles().strokeWidth}
        />
        <Line
          // Star vertical arm
          points={[
            center - radius * 1.2,
            center - radius * 1.2 - radius * 0.35,
            center - radius * 1.2,
            center - radius * 1.2 + radius * 0.35
          ]}
          stroke={getStrokeColor()}
          strokeWidth={getStrokeStyles().strokeWidth}
        />
        <Line
          // Star diagonal arm (NW–SE)
          points={[
            center - radius * 0.95 - radius * 0.25 * 0.9,
            center - radius * 0.95 - radius * 0.25 * 0.9,
            center - radius * 0.95 + radius * 0.25 * 0.9,
            center - radius * 0.95 + radius * 0.25 * 0.9
          ]}
          stroke={getStrokeColor()}
          strokeWidth={getStrokeStyles().strokeWidth}
        />
          <Line
          points={[
            center,
            center + radius * 1,
            center,
            center + radius * 1.6
          ]}
          stroke={getStrokeColor()}
          strokeWidth={getStrokeStyles().strokeWidth}
        />
        <Line
          points={[
            center - radius * 0.4,
            center + radius * 1.3,
            center + radius * 0.4,
            center + radius * 1.3
          ]}
          stroke={getStrokeColor()}
          strokeWidth={getStrokeStyles().strokeWidth}
        />
        </>
      );
    }

    if (shapeType === 'trigender-equal') {      const radius = size * 0.35;
      const center = size / 2;
      return (
        <>
          <Circle
            x={center}
            y={center}
            radius={radius}
            fill={getNodeColor()}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
          />
          <Line
            // Horizontal equality bar
            points={[
              center - radius,
              center,
              center + radius,
              center
            ]}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
          />
          <Line
            // Vertical equality bar
            points={[
              center,
              center - radius,
              center,
              center + radius
            ]}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
          />
          <Line
          points={[
            center + radius * 0.7,
            center - radius * 0.7,
            center + radius * 1.4,
            center - radius * 1.4
          ]}
          stroke={getStrokeColor()}
          strokeWidth={getStrokeStyles().strokeWidth}
          />
          <Line
          points={[
            center + radius * 1.4,
            center - radius * 1.4,
            center + radius * 1.0,
            center - radius * 1.4
          ]}
          stroke={getStrokeColor()}
          strokeWidth={getStrokeStyles().strokeWidth}
          />
          <Line
          points={[
            center + radius * 1.4,
            center - radius * 1.4,
            center + radius * 1.4,
            center - radius * 1.0
          ]}
          stroke={getStrokeColor()}
          strokeWidth={getStrokeStyles().strokeWidth}
          />
          <Line
            points={[
              center,
              center + radius * 1,
              center,
              center + radius * 1.6
            ]}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
          />
          <Line
            points={[
              center - radius * 0.4,
              center + radius * 1.3,
              center + radius * 0.4,
              center + radius * 1.3
            ]}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
          />
          <Line
          // Star horizontal arm
          points={[
            center - radius * 1.2 - radius * 0.35,
            center - radius * 1.2,
            center - radius * 1.2 + radius * 0.35,
            center - radius * 1.2
          ]}
          stroke={getStrokeColor()}
          strokeWidth={getStrokeStyles().strokeWidth}
        />
        <Line
          // Star vertical arm
          points={[
            center - radius * 1.2,
            center - radius * 1.2 - radius * 0.35,
            center - radius * 1.2,
            center - radius * 1.2 + radius * 0.35
          ]}
          stroke={getStrokeColor()}
          strokeWidth={getStrokeStyles().strokeWidth}
        />
        <Line
          // Star diagonal arm (NW–SE)
          points={[
            center - radius * 0.95 - radius * 0.25 * 0.9,
            center - radius * 0.95 - radius * 0.25 * 0.9,
            center - radius * 0.95 + radius * 0.25 * 0.9,
            center - radius * 0.95 + radius * 0.25 * 0.9
          ]}
          stroke={getStrokeColor()}
          strokeWidth={getStrokeStyles().strokeWidth}
        />
        </>
      );
    }

    if (shapeType === 'pangender') {      const radius = size * 0.35;
      const center = size / 2;
      return (
        <>
          <Circle
            x={center}
            y={center}
            radius={radius}
            fill={getNodeColor()}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
          />
          <Line
            points={[
              center - radius,
              center,
              center + radius,
              center
            ]}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
          />      
          <Line
          // Bottom arrow spine
          points={[
            center,
            center + radius,
            center,
            center + radius * 1.7
          ]}
          stroke={getStrokeColor()}
          strokeWidth={getStrokeStyles().strokeWidth}
        />
        <Line
          // Bottom arrow left wing
          points={[
            center - radius * 0.35,
            center + radius * 1.35,
            center,
            center + radius * 1.7
          ]}
          stroke={getStrokeColor()}
          strokeWidth={getStrokeStyles().strokeWidth}
        />
        <Line
          // Bottom arrow right wing
          points={[
            center + radius * 0.35,
            center + radius * 1.35,
            center,
            center + radius * 1.7
          ]}
          stroke={getStrokeColor()}
          strokeWidth={getStrokeStyles().strokeWidth}
        />
        <Line
          // Female cross bar
          points={[
            center - radius * 0.3,
            center + radius * 1.2,
            center + radius * 0.3,
            center + radius * 1.2
          ]}
          stroke={getStrokeColor()}
          strokeWidth={getStrokeStyles().strokeWidth}
        />
  
          <Line
            points={[
            center, center - radius,
            center, center - radius * 1.8
            ]}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
            />
          <Line
            // Star diagonal arm (NW–SE)
            points={[
            size / 2 - size * 0.35 * 0.30,
            size / 2 - size * 0.35 * 1.8 - size * 0.3 * 0.3,
            size / 2 + size * 0.35 * 0.30,
            size / 2 - size * 0.35 * 1.8 + size * 0.3 * 0.3
            ]}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
            />
          <Line
            // Star diagonal arm (NE–SW)
            points={[
            size / 2 - size * 0.35 * 0.30,
            size / 2 - size * 0.35 * 1.8 + size * 0.3 * 0.3,
            size / 2 + size * 0.35 * 0.30,
            size / 2 - size * 0.35 * 1.8 - size * 0.3 * 0.3
            ]}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
          />
        </>
      );
    }

    if (shapeType === 'xenogender') {      const radius = size * 0.35;
      const center = size / 2;
      const arcWidth = radius * 0.6;
      const arcDepth = radius * 0.35;
      return (
        <>
          <Circle
            x={center}
            y={center}
            radius={radius}
            fill={getNodeColor()}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
          />
          <Line
            points={[
              center,
              center + radius * 1,
              center,
              center + radius * 1.4
            ]}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
          />
          <Path
            data={`
              M ${center - arcWidth} ${center + radius * 1.5}
              C ${center - arcWidth * 0.5} ${center + radius * 1.5 - arcDepth},
                ${center - arcWidth * 0.15} ${center + radius * 1.5 - arcDepth},
                ${center} ${center + radius * 1.5}
            `}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
            fillEnabled={false}
          />
          <Path
            data={`
              M ${center} ${center + radius * 1.5}
              C ${center + arcWidth * 0.15} ${center + radius * 1.5 - arcDepth},
                ${center + arcWidth * 0.5} ${center + radius * 1.5 - arcDepth},
                ${center + arcWidth} ${center + radius * 1.5}
            `}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
            fillEnabled={false}
          />
        </>
      );
    }

    if (shapeType === 'travesti') {      const radius = size * 0.35;
      const center = size / 2;
      const circleCenterX = center + radius * 0.2;
      const circleCenterY = center + radius * 0.15;
      const barBaseX = circleCenterX - radius * 0.8;
      const barBaseY = circleCenterY - radius * 0.6;
      const barTopX = barBaseX - radius * 0.6;
      const barTopY = barBaseY - radius * 0.6;
      const barCapLength = radius * 0.6;
      return (
        <>
          <Circle
            x={circleCenterX}
            y={circleCenterY}
            radius={radius}
            fill={getNodeColor()}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
          />
          <Line
            points={[
              circleCenterX - radius * 0.7,
              circleCenterY - radius * 0.6,
              barTopX,
              barTopY
            ]}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
          />
          <Line
            points={[
              barTopX - barCapLength * 0.6,
              barTopY + barCapLength * 0.6,
              barTopX + barCapLength * 0.5,
              barTopY - barCapLength * 0.7
            ]}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
          />
        </>
      );
    }

    if (shapeType === 'third-gender') {      const radius = size * 0.35;
      const center = size / 2;
      const circleCenterX = center + radius * 0.5;
      const circleCenterY = center;
      const circleEdgeX = circleCenterX - radius;
      const circleEdgeY = circleCenterY;
      const stemLength = radius * 0.4;
      const stemEndX = circleEdgeX - stemLength;
      const wingLength = radius * 0.55;
      return (
        <>
          <Circle
            x={circleCenterX}
            y={circleCenterY}
            radius={radius}
            fill={getNodeColor()}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
          />
          <Line
          points={[
            center - radius * 0.5,
            center,
            center - radius * 0.9,
            center
          ]}
          stroke={getStrokeColor()}
          strokeWidth={getStrokeStyles().strokeWidth}
        />
          <Line
            // Wing wedge
            points={[
              stemEndX,
              circleEdgeY,
              stemEndX - wingLength,
              circleEdgeY - wingLength * 0.6,
              stemEndX - wingLength,
              circleEdgeY + wingLength * 0.6
            ]}
            closed
            fillEnabled={false}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
          />
        </>
      );
    }

    if (shapeType === 'hijra') {      const radius = size * 0.35;
      const center = size / 2;
      const circleEdgeX = center - radius * 1.1;
      const circleEdgeY = center;
      const stemLength = radius * 0.35;
      const stemEndX = circleEdgeX - stemLength;
      const wingLength = radius * 0.5;
      
      const outerR = radius * 1.1;
      const innerR = radius * 0.9;
      const offset = radius * 0.2;
      return (
        <>
          <Line
            points={[
              circleEdgeX,
              circleEdgeY,
              stemEndX,
              circleEdgeY
            ]}
            fill={getNodeColor()}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
          />
          <Line
            // Wing wedge
            points={[
              stemEndX,
              circleEdgeY,
              stemEndX - wingLength,
              circleEdgeY - wingLength * 0.6,
              stemEndX - wingLength,
              circleEdgeY + wingLength * 0.6
            ]}
            closed
            fill={getNodeColor()}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
          /> 
          <Path
            data={`
              M ${center + outerR} ${center}
              A ${outerR} ${outerR} 0 1 0 ${center - outerR} ${center}
              A ${outerR} ${outerR} 0 1 0 ${center + outerR} ${center}
              M ${center + offset + innerR} ${center}
              A ${innerR} ${innerR} 0 1 1 ${center + offset - innerR} ${center}
              A ${innerR} ${innerR} 0 1 1 ${center + offset + innerR} ${center}
              Z
            `}
            fill={getStrokeColor()}
            strokeEnabled={false}
          />
        </>
      );
    }

    if (shapeType === 'neurogender') {      const radius = size * 0.35
      const center = size / 2;
      return (
        <>
          <Line
            points={[
              center, center - radius,
              center + radius, center,
              center, center + radius,
              center - radius, center
            ]}
            closed
            fill={getNodeColor()}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
          />
          <Line
          points={[
            center,
            center + radius * 1,
            center,
            center + radius * 1.7
          ]}
          stroke={getStrokeColor()}
          strokeWidth={getStrokeStyles().strokeWidth}
        />
        <Line
          points={[
            center - radius * 0.4,
            center + radius * 1.3,
            center + radius * 0.4,
            center + radius * 1.3
          ]}
          stroke={getStrokeColor()}
          strokeWidth={getStrokeStyles().strokeWidth}
        />
        </>
      );
    }

    if (shapeType === 'detragender') {
      return (
        <>
          <Circle
            x={size / 2}
            y={size / 2}
            radius={size * 0.48}
            fill={getNodeColor()}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
          />
          <Circle
            x={size / 2}
            y={size / 2 - size * 0.48 * 0.45}
            radius={size * 0.48 * 0.56}
            fill={getNodeColor()}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
          />
          <Line
            points={[
              size / 2, size / 2 + size * 0.60 * 0.1,          // was 0.2 → 0.1
              size / 2 + size * 0.60 * 0.35, size / 2 + size * 0.60 * 0.45, // 0.55 → 0.45
              size / 2, size / 2 + size * 0.60 * 0.8,          // 0.9 → 0.8
              size / 2 - size * 0.60 * 0.35, size / 2 + size * 0.60 * 0.45 // 0.55 → 0.45
            ]}
            closed
            fillEnabled={false}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
          />
        </>
      );
    }

    if (shapeType === 'centrgender') {      
      const radius = size * 0.35;
      const center = size / 2;
      const arrowHead = radius * 0.7;
      const rightArrowLen = radius * 0.2;
      const rightWingOffset = radius * 0.3;
      return (
        <>
          <Circle
            x={center}
            y={center}
            radius={radius}
            fill={getNodeColor()}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
          />
          <Line
            // Vertical axis through the circle
            points={[
              center,
              center - radius * 1.5,
              center,
              center + radius * 1.5
            ]}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
          />
          <Line
            // Top arrow spine
            points={[
              center,
              center - radius,
              center,
              center - radius - arrowHead
            ]}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
          />
          <Line
            // Top arrow left wing
            points={[
              center - radius * 0.35,
              center - radius - arrowHead * 0.5,
              center,
              center - radius - arrowHead
            ]}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
          />
          <Line
            // Top arrow right wing
            points={[
              center + radius * 0.35,
              center - radius - arrowHead * 0.5,
              center,
              center - radius - arrowHead
            ]}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
          />
          <Line
            // Bottom arrow spine
            points={[
              center,
              center + radius,
              center,
              center + radius + arrowHead
            ]}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
          />
          <Line
            // Bottom arrow left wing
            points={[
              center - radius * 0.35,
              center + radius + arrowHead * 0.5,
              center,
              center + radius + arrowHead
            ]}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
          />
          <Line
            // Bottom arrow right wing
            points={[
              center + radius * 0.35,
              center + radius + arrowHead * 0.5,
              center,
              center + radius + arrowHead
            ]}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
          />
          <Line
            points={[
              center + radius,
              center - rightArrowLen,
              center + radius + rightWingOffset,
              center - rightArrowLen + rightWingOffset
            ]}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
          />
          <Line
            points={[
              center + radius,
              center - rightArrowLen,
              center + radius - rightWingOffset,
              center - rightArrowLen + rightWingOffset
            ]}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
          />
          <Line
            // Left arrow right wing
            points={[
              center - radius,
              center + rightArrowLen,
              center - radius - rightWingOffset,
              center + rightArrowLen - rightWingOffset
            ]}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
          />
          <Line
            // Left arrow left wing
            points={[
              center - radius,
              center + rightArrowLen,
              center - radius + rightWingOffset,
              center + rightArrowLen - rightWingOffset
            ]}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
          />
          <Line
            // Female cross bar
            points={[
              center - radius * 0.3, center - radius * 1.2,
              center + radius * 0.3, center - radius * 1.2
            ]}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
          />
          <Line
            // Female cross bar
            points={[
              center - radius * 0.3, center + radius * 1.2,
              center + radius * 0.3, center + radius * 1.2
            ]}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
          />
        </>
      );
    }

    if (shapeType === 'isogender') {
      const radius = size * 0.35;
      const center = size / 2;
      const arrowHead = radius * 0.5;
      const rightArrowLen = radius * 0.2;
      const rightWingOffset = radius * 0.3;
      const topCrossArm = radius * 0.35;
      const topStemEndY = center - radius - arrowHead * 0.8;
      const topStemEndX = center + radius + arrowHead * 0.8;
      return (
        <>
          <Circle
            x={center}
            y={center}
            radius={radius}
            fill={getNodeColor()}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
          />
          <Line
            // Vertical axis through the circle
            points={[
              center,
              center - radius * 1.5,
              center,
              center + radius * 1.5
            ]}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
          />
          <Line
            points={[
              center - topCrossArm * 0.6,
              topStemEndY - topCrossArm * 0.6,
              center + topCrossArm * 0.6,
              topStemEndY + topCrossArm * 0.6
            ]}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
          />
          <Line
            points={[
              center - topCrossArm * 0.6,
              topStemEndY + topCrossArm * 0.6,
              center + topCrossArm * 0.6,
              topStemEndY - topCrossArm * 0.6
            ]}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
          />
          <Line
            points={[
              center - topCrossArm * 0.6,
              topStemEndX - topCrossArm * 0.6,
              center + topCrossArm * 0.6,
              topStemEndX + topCrossArm * 0.6
            ]}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
          />
          <Line
            points={[
              center - topCrossArm * 0.6,
              topStemEndX + topCrossArm * 0.6,
              center + topCrossArm * 0.6,
              topStemEndX - topCrossArm * 0.6
            ]}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
          />
          <Line
            points={[
              center + radius,
              center - rightArrowLen,
              center + radius + rightWingOffset,
              center - rightArrowLen + rightWingOffset
            ]}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
          />
          <Line
            points={[
              center + radius,
              center - rightArrowLen,
              center + radius - rightWingOffset,
              center - rightArrowLen + rightWingOffset
            ]}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
          />
          <Line
            // Left arrow right wing
            points={[
              center - radius,
              center + rightArrowLen,
              center - radius - rightWingOffset,
              center + rightArrowLen - rightWingOffset
            ]}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
          />
          <Line
            // Left arrow left wing
            points={[
              center - radius,
              center + rightArrowLen,
              center - radius + rightWingOffset,
              center + rightArrowLen - rightWingOffset
            ]}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
          />
        </>
      );
    }

    if (shapeType === 'ultergender') {      const radius = size * 0.35;
      const center = size / 2;
      return (
        <>
          <Circle
            x={center}
            y={center}
            radius={radius}
            fill={getNodeColor()}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
          />      
          <Line
            // T stem
            points={[
              center + radius * 0.75,
              center - radius * 0.65,
              center + radius * 1.5,
              center - radius * 1.4
            ]}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
          />
          <Line
            // T crossbar (perpendicular at the stem tip)
            points={[
              center + radius * 1.5 - radius * 0.4 * Math.SQRT1_2,
              center - radius * 1.4 - radius * 0.4 * Math.SQRT1_2,
              center + radius * 1.5 + radius * 0.4 * Math.SQRT1_2,
              center - radius * 1.4 + radius * 0.4 * Math.SQRT1_2
            ]}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
          /><Line
          // Bottom arrow spine
          points={[
            center,
            center + radius,
            center,
            center + radius * 1.7
          ]}
          stroke={getStrokeColor()}
          strokeWidth={getStrokeStyles().strokeWidth}
          />
          <Line
          // Bottom arrow left wing
          points={[
            center - radius * 0.35,
            center + radius * 1.2,
            center,
            center + radius * 1.4
          ]}
          stroke={getStrokeColor()}
          strokeWidth={getStrokeStyles().strokeWidth}
          />
          <Line
          // Bottom arrow right wing
          points={[
            center + radius * 0.35,
            center + radius * 1.2,
            center,
            center + radius * 1.4
          ]}
          stroke={getStrokeColor()}
          strokeWidth={getStrokeStyles().strokeWidth}
          />
        </>
      );
    }

    if (shapeType === 'ultergender-equal') {      const radius = size * 0.35;
      const center = size / 2;
      return (
        <>
          <Circle
            x={center}
            y={center}
            radius={radius}
            fill={getNodeColor()}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
          />
          <Line
            points={[
              center - radius,
              center,
              center + radius,
              center
            ]}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
          />
          <Line
            // Vertical equality bar
            points={[
              center,
              center - radius,
              center,
              center + radius
            ]}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
          />
          <Line
          points={[
            center + radius * 0.7,
            center - radius * 0.7,
            center + radius * 1.4,
            center - radius * 1.4
          ]}
          stroke={getStrokeColor()}
          strokeWidth={getStrokeStyles().strokeWidth}
          />
          <Line
          points={[
            center + radius * 1.4,
            center - radius * 1.4,
            center + radius * 1.0,
            center - radius * 1.4
          ]}
          stroke={getStrokeColor()}
          strokeWidth={getStrokeStyles().strokeWidth}
          />
          <Line
          points={[
            center + radius * 1.4,
            center - radius * 1.4,
            center + radius * 1.4,
            center - radius * 1.0
          ]}
          stroke={getStrokeColor()}
          strokeWidth={getStrokeStyles().strokeWidth}
          />
          <Line
          points={[
            center,
            center + radius * 1,
            center,
            center + radius * 1.6
          ]}
          stroke={getStrokeColor()}
          strokeWidth={getStrokeStyles().strokeWidth}
        />
        <Line
          points={[
            center - radius * 0.4,
            center + radius * 1.3,
            center + radius * 0.4,
            center + radius * 1.3
          ]}
          stroke={getStrokeColor()}
          strokeWidth={getStrokeStyles().strokeWidth}
        /><Line
        // Diagonal stem running northwest from circle
        points={[
          center - radius * 0.70,
          center - radius * 0.70,
          center - radius * 1.38,
          center - radius * 1.38
        ]}
        stroke={getStrokeColor()}
        strokeWidth={getStrokeStyles().strokeWidth}
        />
        <Line
        // Arrowhead leg on the north side of the stem tip
        points={[
          center - radius * 1.38,
          center - radius * 1.38,
          center - radius * 1.38 + radius * 0.35 * Math.SQRT1_2 + radius * 0.38 * 0.80 * Math.SQRT1_2,
          center - radius * 1.38 + radius * 0.35 * Math.SQRT1_2 - radius * 0.38 * 0.80 * Math.SQRT1_2
        ]}
        stroke={getStrokeColor()}
        strokeWidth={getStrokeStyles().strokeWidth}
        />
        <Line
        // Arrowhead leg on the west side of the stem tip
        points={[
          center - radius * 1.38,
          center - radius * 1.38,
          center - radius * 1.38 + radius * 0.35 * Math.SQRT1_2 - radius * 0.38 * 0.80 * Math.SQRT1_2,
          center - radius * 1.38 + radius * 0.35 * Math.SQRT1_2 + radius * 0.38 * 0.80 * Math.SQRT1_2
        ]}
        stroke={getStrokeColor()}
        strokeWidth={getStrokeStyles().strokeWidth}
        />
        <Line
        // Diagonal accent mirroring transgender star arm
        points={[
          center - radius * 0.95 - radius * 0.25,
          center - radius * 0.95 + radius * 0.25,
          center - radius * 0.95 + radius * 0.25,
          center - radius * 0.95 - radius * 0.25
        ]}
        stroke={getStrokeColor()}
        strokeWidth={getStrokeStyles().strokeWidth}
        />
        </>
      );
    }

    if (shapeType === 'male-equal') {      const radius = size * 0.35;
      const center = size / 2;
      return (
        <>
          <Circle
            x={center}
            y={center}
            radius={radius}
            fill={getNodeColor()}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
          />
          <Line
            points={[
              center - radius,
              center,
              center + radius,
              center
            ]}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
          />
          <Line
            points={[
              center,
              center - radius,
              center,
              center + radius
            ]}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
          />
          <Line
          points={[
            center + radius * 0.7,
            center - radius * 0.7,
            center + radius * 1.4,
            center - radius * 1.4
          ]}
          stroke={getStrokeColor()}
          strokeWidth={getStrokeStyles().strokeWidth}
          />
          <Line
          points={[
            center + radius * 1.4,
            center - radius * 1.4,
            center + radius * 1.0,
            center - radius * 1.4
          ]}
          stroke={getStrokeColor()}
          strokeWidth={getStrokeStyles().strokeWidth}
          />
          <Line
          points={[
            center + radius * 1.4,
            center - radius * 1.4,
            center + radius * 1.4,
            center - radius * 1.0
          ]}
          stroke={getStrokeColor()}
          strokeWidth={getStrokeStyles().strokeWidth}
          />
        </>
      );
    }

    if (shapeType === 'questioning') {      const radius = size * 0.35;
      const center = size / 2;
      const arcRotationDeg = 230;
      const arcStartAngleRad = (arcRotationDeg * Math.PI) / 180;
      const arrowArmSpread = Math.PI / 4;
      const arrowArmLength = radius * 0.35;
      const arcStartX = center + radius * Math.cos(arcStartAngleRad);
      const arcStartY = center + radius * Math.sin(arcStartAngleRad);
      const arrowBaseAngle = arcStartAngleRad + Math.PI / 2;
      return (
        <>
        <Rect
          x={center - radius * 0.7}
          y={center - radius * 1.2}
          width={radius * 1.8}
          height={radius * 2.8}
          strokeWidth={getStrokeStyles().strokeWidth}
        />
        <Arc
        x={center + radius * 0}
        y={center}
        innerRadius={radius * 1}
        outerRadius={radius * 1}
        angle={-140}
        rotation={230}
        fill={getNodeColor()}
        stroke={getStrokeColor()}
        strokeWidth={getStrokeStyles().strokeWidth}
      />
        <Line
          points={[
            center,
            center + radius * 1,
            center,
            center + radius * 1.6
          ]}
          stroke={getStrokeColor()}
          strokeWidth={getStrokeStyles().strokeWidth}
        />
        <Line
          points={[
            arcStartX,
            arcStartY,
            arcStartX + arrowArmLength * Math.cos(arrowBaseAngle + arrowArmSpread),
            arcStartY + arrowArmLength * Math.sin(arrowBaseAngle + arrowArmSpread)
          ]}
          stroke={getStrokeColor()}
          strokeWidth={getStrokeStyles().strokeWidth}
        />
        <Line
          points={[
            arcStartX,
            arcStartY,
            arcStartX + arrowArmLength * Math.cos(arrowBaseAngle - arrowArmSpread),
            arcStartY + arrowArmLength * Math.sin(arrowBaseAngle - arrowArmSpread)
          ]}
          stroke={getStrokeColor()}
          strokeWidth={getStrokeStyles().strokeWidth}
        />
        <Line
          points={[
            center - radius * 0.4,
            center + radius * 1.3,
            center + radius * 0.4,
            center + radius * 1.3
          ]}
          stroke={getStrokeColor()}
          strokeWidth={getStrokeStyles().strokeWidth}
        />
        </>
      );
    }

    if (shapeType === 'queer') {      const radius = size * 0.35;
      const center = size / 2;
      return (
        <>
        <Rect
          x={center - radius * 1.3}
          y={center - radius * 1.2}
          width={radius * 2.2}
          height={radius * 2.8}
          strokeWidth={getStrokeStyles().strokeWidth}
        />
          <Line
            points={[
              center - radius * 0.8,
              center - radius,
              center + radius * 0.4,
              center + radius
            ]}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
          />
          <Line
            points={[
              center - radius * 0.8,
              center - radius * 1,
              center - radius * 1.3,
              center - radius * 0.6
            ]}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
          />
          <Line
            points={[
              center + radius * 0.4,
              center + radius * 1,
              center + radius * 0.9,
              center + radius * 0.65
            ]}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
          />
          <Line
            points={[
              center - radius * 0.2,
              center,
              center - radius * 1,
              center + radius * 1
            ]}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
          />
        </>
      );
    }

    if (shapeType === 'polyamorous') {
      const radius = size * 0.35;
      const center = size / 2;
      const glyphRadius = radius * 1.2;
      const heartPath = `
        M ${center} ${center + glyphRadius * 0.04}
        C ${center + glyphRadius * 0.85} ${center - glyphRadius * 0.48}, ${center + glyphRadius * 1.2} ${center + glyphRadius * 0.58}, ${center} ${center + glyphRadius * 1.12}
        C ${center - glyphRadius * 1.2} ${center + glyphRadius * 0.58}, ${center - glyphRadius * 0.85} ${center - glyphRadius * 0.48}, ${center} ${center + glyphRadius * 0.04}
        Z
      `;
    
      const infinityCenterY = center + glyphRadius * 0.35;
      const infinityWidth = glyphRadius;
      const infinityHeight = glyphRadius * 0.55;
      const loopGap = glyphRadius * 0.14;
      const loopReach = loopGap + infinityWidth;
      const centerCtrlOffset = loopGap + infinityWidth * 0.42;
      const infinityPath = `
        M ${center} ${infinityCenterY}
        C ${center - centerCtrlOffset} ${infinityCenterY - infinityHeight},
          ${center - loopReach} ${infinityCenterY - infinityHeight * 0.4},
          ${center - loopReach} ${infinityCenterY}
        C ${center - loopReach} ${infinityCenterY + infinityHeight * 0.4},
          ${center - centerCtrlOffset} ${infinityCenterY + infinityHeight},
          ${center} ${infinityCenterY}
        C ${center + centerCtrlOffset} ${infinityCenterY - infinityHeight},
          ${center + loopReach} ${infinityCenterY - infinityHeight * 0.4},
          ${center + loopReach} ${infinityCenterY}
        C ${center + loopReach} ${infinityCenterY + infinityHeight * 0.4},
          ${center + centerCtrlOffset} ${infinityCenterY + infinityHeight},
          ${center} ${infinityCenterY}
      `;
    
      return (
        <>
          <Path
            data={heartPath}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
            fill={getNodeColor()}
          />
          <Path
            data={infinityPath}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
            fillEnabled={false}
          />
        </>
      );
    }
    

    if (shapeType === 'self-partnering') {
      const radius = size * 0.35;
      const center = size / 2;
      const glyphRadius = radius * 1.2;

      const heartPath = `
        M ${center} ${center + glyphRadius * 0.05}
        C ${center + glyphRadius * 0.85} ${center - glyphRadius * 0.45}, ${center + glyphRadius * 1.2} ${center + glyphRadius * 0.55}, ${center} ${center + glyphRadius * 1.08}
        C ${center - glyphRadius * 1.2} ${center + glyphRadius * 0.55}, ${center - glyphRadius * 0.85} ${center - glyphRadius * 0.45}, ${center} ${center + glyphRadius * 0.05}
        Z
      `;

      const infinityCenterY = center + glyphRadius * 0.4;
      const infinityWidth = glyphRadius * 0.9;
      const infinityHeight = glyphRadius * 0.5;
      const loopGap = glyphRadius * 0.12;
      const loopReach = loopGap + infinityWidth;
      const centerCtrlOffset = loopGap + infinityWidth * 0.4;
      const infinityPath = `
        M ${center} ${infinityCenterY}
        C ${center - centerCtrlOffset} ${infinityCenterY - infinityHeight},
          ${center - loopReach} ${infinityCenterY - infinityHeight * 0.4},
          ${center - loopReach} ${infinityCenterY}
        C ${center - loopReach} ${infinityCenterY + infinityHeight * 0.4},
          ${center - centerCtrlOffset} ${infinityCenterY + infinityHeight},
          ${center} ${infinityCenterY}
        C ${center + centerCtrlOffset} ${infinityCenterY - infinityHeight},
          ${center + loopReach} ${infinityCenterY - infinityHeight * 0.4},
          ${center + loopReach} ${infinityCenterY}
        C ${center + loopReach} ${infinityCenterY + infinityHeight * 0.4},
          ${center + centerCtrlOffset} ${infinityCenterY + infinityHeight},
          ${center} ${infinityCenterY}
      `;

      return (
        <>
          <Path
            data={heartPath}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
            fill={getNodeColor()}
          />
          <Path
            data={infinityPath}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
            fillEnabled={false}
          />
          <Line
            points={[
              center - glyphRadius,
              center + glyphRadius * 0.3,
              center - glyphRadius - glyphRadius * 0.2,
              center + glyphRadius * 0.3 + glyphRadius * 0.2
            ]}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
          />
          <Line
            points={[
              center - glyphRadius,
              center + glyphRadius * 0.3,
              center - glyphRadius + glyphRadius * 0.2,
              center + glyphRadius * 0.3 + glyphRadius * 0.2
            ]}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
          />
          <Line
            points={[
              center + glyphRadius,
              center + glyphRadius * 0.55,
              center + glyphRadius - glyphRadius * 0.2,
              center + glyphRadius * 0.55 - glyphRadius * 0.2
            ]}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
          />
          <Line
            points={[
              center + glyphRadius,
              center + glyphRadius * 0.55,
              center + glyphRadius + glyphRadius * 0.2,
              center + glyphRadius * 0.55 - glyphRadius * 0.2
            ]}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
          />
        </>
      );
    }

    if (shapeType === 'nonamorous') {
      const radius = size * 0.35;
      const center = size / 2;
      const arcPath = `
        M ${center - radius * 1.1} ${center - radius * 0.2}
        C ${center - radius * 0.75} ${center - radius * 2.1},
          ${center - radius * 0.45} ${center + radius * 0.95},
          ${center} ${center + radius * 1.1}
        C ${center + radius * 0.45} ${center + radius * 1.0},
          ${center + radius * 0.8} ${center - radius * 0.1},
          ${center + radius * 0.9} ${center - radius * 0.95}
      `;
      return (
        <>
        <Rect
          x={center - radius * 1.1}
          y={center - radius * 1}
          width={radius * 2.1}
          height={radius * 2.1}
          strokeWidth={getStrokeStyles().strokeWidth}
        />
        <Path
          data={arcPath}
          stroke={getStrokeColor()}
          strokeWidth={getStrokeStyles().strokeWidth}
        />
        </>
      );
    }

    if (shapeType === 'ambiamorous') {
      const radius = size * 0.35;
      const center = size / 2;
      return (
        <>
          <Text
            x={center - radius * 1}
            y={center - radius * 1.3}
            text="α"
            fontStyle="normal"
            fontSize={radius * 3}
            fill={getStrokeColor()}
          />
        </>
      );
    }

    if (shapeType === 'polyflexible') {      const radius = size * 0.35;
      const center = size / 2;
      return (
        <>
          <Text
            x={center + radius * 0.75}
            y={center - radius * 0.95}
            text="µ"
            fontSize={radius * 0.9}
            fill={getStrokeColor()}
          />
          <Text
            x={center - radius * 1.5}
            y={center - radius * 1}
            text="π"
            fontStyle="normal"
            fontSize={radius * 3}
            fill={getStrokeColor()}
          />
        </>
      );
    }

    if (shapeType === 'monoflexible') {      const radius = size * 0.35;
      const center = size / 2;
      return (
        <>
        <Text
          x={center + radius * 0.5}
          y={center - radius * 1}
          text="π"
          fontSize={radius * 0.9}
          fill={getStrokeColor()}
        />
        <Text
          x={center - radius * 1}
          y={center - radius * 1}
          text="µ"
          fontStyle="normal"
          fontSize={radius * 2.5}
          fill={getStrokeColor()}
        />
        </>
      );
    }

    if (shapeType === 'female-equal') {      const radius = size * 0.35;
      const center = size / 2;
      return (
        <>
          <Circle
            x={center}
            y={center}
            radius={radius}
            fill={getNodeColor()}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
          />
          <Line
            points={[
              center - radius,
              center,
              center + radius,
              center
            ]}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
          />
          <Line
            points={[
              center,
              center - radius,
              center,
              center + radius
            ]}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
          />
          <Line
          points={[
            center,
            center + radius * 1,
            center,
            center + radius * 1.6
          ]}
          stroke={getStrokeColor()}
          strokeWidth={getStrokeStyles().strokeWidth}
        />
        <Line
          points={[
            center - radius * 0.4,
            center + radius * 1.3,
            center + radius * 0.4,
            center + radius * 1.3
          ]}
          stroke={getStrokeColor()}
          strokeWidth={getStrokeStyles().strokeWidth}
        />
        </>
      );
    }

    if (shapeType === 'intersex') {      
      const radius = size * 0.35;
      const center = size / 2;
      const circleCenterY = center;
      const arcStartX = center - radius * 0.7;
      const arcEndX = center + radius * 0.7;
      const arcBaselineY = circleCenterY - radius * 1.2;
      const arcControlY = circleCenterY - radius * 0.9;
      return (
        <>
          <Circle
          x={center}
          y={circleCenterY}
          radius={radius}
          fill={getNodeColor()}
          stroke={getStrokeColor()}
          strokeWidth={getStrokeStyles().strokeWidth}
        />
        <Path
          data={`
            M ${arcStartX} ${arcBaselineY}
            Q ${center} ${arcControlY}, ${arcEndX} ${arcBaselineY}
          `}
          stroke={getStrokeColor()}
          strokeWidth={getStrokeStyles().strokeWidth}
          fillEnabled={false}
        />
        <Line
            points={[
              center,
              center + radius * 1,
              center,
              center + radius * 1.6
            ]}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
          />
          <Line
            points={[
              center - radius * 0.4,
              center + radius * 1.3,
              center + radius * 0.4,
              center + radius * 1.3
            ]}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
          />
        </>
      );
    }

    if (shapeType === 'classic-female') {      const radius = size * 0.35
      const center = size / 2;
      return (
        <>
          <Circle
            x={center}
            y={center - radius * 0.4}
            radius={radius}
            fill={getNodeColor()}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
          />
          <Line
          points={[
            center,
            center + radius * 0.6,
            center,
            center + radius * 1.4
          ]}
          stroke={getStrokeColor()}
          strokeWidth={getStrokeStyles().strokeWidth}
          />
          <Line
          points={[
            center - radius * 0.4,
            center + radius * 1.0,
            center + radius * 0.4,
            center + radius * 1.0
          ]}
          stroke={getStrokeColor()}
          strokeWidth={getStrokeStyles().strokeWidth}
          />
        </>
      );
    }

    if (shapeType === 'classic-male') {      const radius = size * 0.35
      const center = size / 2;
      return (
        <>
          <Circle
            x={center}
            y={center}
            radius={radius}
            fill={getNodeColor()}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
          />
          <Line
            points={[
              center + radius * 0.7,
              center - radius * 0.7,
              center + radius * 1.4,
              center - radius * 1.4
            ]}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
          />
          <Line
            points={[
              center + radius * 1.4,
              center - radius * 1.4,
              center + radius * 1.0,
              center - radius * 1.4
            ]}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
          />
          <Line
            points={[
              center + radius * 1.4,
              center - radius * 1.4,
              center + radius * 1.4,
              center - radius * 1.0
            ]}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
          />
        </>
      );
    }

    if (shapeType === 'demigender') {      const radius = size * 0.35;
      const center = size / 2;
      const circleCenterY = center + radius * 0.25;
      const stemStartY = circleCenterY - radius;
      const stemEndY = stemStartY - radius * 0.9;
      const arrowArm = radius * 0.4;
      return (
        <>
          <Circle
            x={center}
            y={circleCenterY}
            radius={radius}
            fill={getNodeColor()}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
          />
          <Line
            points={[
              center,
              stemStartY,
              center,
              stemEndY
            ]}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
          />
          <Line
            points={[
              center,
              stemEndY,
              center - arrowArm * 1.3,
              stemEndY + arrowArm * 0.9
            ]}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
          />
          <Line
            points={[
              center,
              stemEndY,
              center - arrowArm * 1.3,
              stemEndY - arrowArm * 0.9
            ]}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
          />
        </>
      );
    }

    if (shapeType === 'androgyne') {      const radius = size * 0.35;
      const center = size / 2;
      return (
        <>
          <Circle
            x={center}
            y={center}
            radius={radius}
            fill={getNodeColor()}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
          /><Line
          // Diagonal stem running northwest from circle
          points={[
            center - radius * 0.70,
            center - radius * 0.70,
            center - radius * 1.38,
            center - radius * 1.38
          ]}
          stroke={getStrokeColor()}
          strokeWidth={getStrokeStyles().strokeWidth}
          />
          <Line
          // Arrowhead leg on the north side of the stem tip
          points={[
            center - radius * 1.38,
            center - radius * 1.38,
            center - radius * 1.38 + radius * 0.35 * Math.SQRT1_2 + radius * 0.38 * 0.80 * Math.SQRT1_2,
            center - radius * 1.38 + radius * 0.35 * Math.SQRT1_2 - radius * 0.38 * 0.80 * Math.SQRT1_2
          ]}
          stroke={getStrokeColor()}
          strokeWidth={getStrokeStyles().strokeWidth}
          />
          <Line
          // Arrowhead leg on the west side of the stem tip
          points={[
            center - radius * 1.38,
            center - radius * 1.38,
            center - radius * 1.38 + radius * 0.35 * Math.SQRT1_2 - radius * 0.38 * 0.80 * Math.SQRT1_2,
            center - radius * 1.38 + radius * 0.35 * Math.SQRT1_2 + radius * 0.38 * 0.80 * Math.SQRT1_2
          ]}
          stroke={getStrokeColor()}
          strokeWidth={getStrokeStyles().strokeWidth}
          />
          <Line
          // Diagonal accent mirroring transgender star arm
          points={[
            center - radius * 0.95 - radius * 0.25,
            center - radius * 0.95 + radius * 0.25,
            center - radius * 0.95 + radius * 0.25,
            center - radius * 0.95 - radius * 0.25
          ]}
          stroke={getStrokeColor()}
          strokeWidth={getStrokeStyles().strokeWidth}
          />
        </>
      );
    }

    if (shapeType === 'demiboy') {      const radius = size * 0.35;
      const center = size / 2;
      return (
        <>
          <Circle
            x={center}
            y={center}
            radius={radius}
            fill={getNodeColor()}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
          />
          <Line
          points={[
            center + radius * 0.7,
            center - radius * 0.7,
            center + radius * 1.4,
            center - radius * 1.4
          ]}
          stroke={getStrokeColor()}
          strokeWidth={getStrokeStyles().strokeWidth}
          />
          <Line
          points={[
            center + radius * 1.4,
            center - radius * 1.4,
            center + radius * 1.0,
            center - radius * 1.4
          ]}
          stroke={getStrokeColor()}
          strokeWidth={getStrokeStyles().strokeWidth}
          />
          <Line
          points={[
            center + radius * 1.4,
            center - radius * 1.4,
            center + radius * 1.4,
            center - radius * 1.0
          ]}
          stroke={getStrokeColor()}
          strokeWidth={getStrokeStyles().strokeWidth}
          />
        </>
      );
    }

    if (shapeType === 'demigirl') {      const radius = size * 0.35;
      const center = size / 2;
      return (
        <>
          <Circle
            x={center}
            y={center - radius * 0.1}
            radius={radius}
            fill={getNodeColor()}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
          />
          <Line
          points={[
            center,
            center + radius * 0.9,
            center,
            center + radius * 1.6
          ]}
          stroke={getStrokeColor()}
          strokeWidth={getStrokeStyles().strokeWidth}
          />
          <Line
          points={[
            center - radius * 0.4,
            center + radius * 1.3,
            center + radius * 0.0,
            center + radius * 1.3
          ]}
          stroke={getStrokeColor()}
          strokeWidth={getStrokeStyles().strokeWidth}
          />
        </>
      );
    }

    if (shapeType === 'intergender') {      const radius = size * 0.35;
      const center = size / 2;
      return (
        <>
          <Circle
            x={center}
            y={center - radius * 0.05}
            radius={radius}
            fill={getNodeColor()}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
          />
          <Line
          points={[
            center + radius * 0.55,
            center + radius * 0.75,
            center + radius * 1.1,
            center + radius * 1.3
          ]}
          stroke={getStrokeColor()}
          strokeWidth={getStrokeStyles().strokeWidth}
        />
        <Line
          points={[
            center + radius * 0.7,
            center + radius * 0.58,
            center + radius * 1.3,
            center + radius * 1.2
          ]}
          stroke={getStrokeColor()}
          strokeWidth={getStrokeStyles().strokeWidth}
        />
        
          <Line
          points={[
            center + radius * 0.5,
            center + radius * 1.4,
            center + radius * 0.85,
            center + radius * 1.05
          ]}
          stroke={getStrokeColor()}
          strokeWidth={getStrokeStyles().strokeWidth}
        />
          <Line
          points={[
            center + radius * 1.3,
            center + radius * 1.2,
            center + radius * 1.3,
            center - radius * - 0.7
          ]}
          stroke={getStrokeColor()}
          strokeWidth={getStrokeStyles().strokeWidth}
        />
        </>
      );
    }

    if (shapeType === 'transgender-classic') {      const radius = size * 0.35;
      const center = size / 2;
      return (
        <>
          <Circle
            x={center}
            y={center}
            radius={radius}
            fill={getNodeColor()}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
          />
          <Line
          points={[
            center + radius * 0.7,
            center - radius * 0.7,
            center + radius * 1.4,
            center - radius * 1.4
          ]}
          stroke={getStrokeColor()}
          strokeWidth={getStrokeStyles().strokeWidth}
          />
          <Line
          points={[
            center + radius * 1.4,
            center - radius * 1.4,
            center + radius * 1.0,
            center - radius * 1.4
          ]}
          stroke={getStrokeColor()}
          strokeWidth={getStrokeStyles().strokeWidth}
          />
          <Line
          points={[
            center + radius * 1.4,
            center - radius * 1.4,
            center + radius * 1.4,
            center - radius * 1.0
          ]}
          stroke={getStrokeColor()}
          strokeWidth={getStrokeStyles().strokeWidth}
          />
          <Line
            points={[
              center,
              center + radius * 1,
              center,
              center + radius * 1.6
            ]}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
          />
          <Line
            points={[
              center - radius * 0.4,
              center + radius * 1.3,
              center + radius * 0.4,
              center + radius * 1.3
            ]}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
          />      
          <Line
          // Star horizontal arm
          points={[
            center - radius * 1.2 - radius * 0.35,
            center - radius * 1.2,
            center - radius * 1.2 + radius * 0.35,
            center - radius * 1.2
          ]}
          stroke={getStrokeColor()}
          strokeWidth={getStrokeStyles().strokeWidth}
        />
        <Line
          // Star vertical arm
          points={[
            center - radius * 1.2,
            center - radius * 1.2 - radius * 0.35,
            center - radius * 1.2,
            center - radius * 1.2 + radius * 0.35
          ]}
          stroke={getStrokeColor()}
          strokeWidth={getStrokeStyles().strokeWidth}
        />
        <Line
          // Star diagonal arm (NW–SE)
          points={[
            center - radius * 0.95 - radius * 0.25 * 0.9,
            center - radius * 0.95 - radius * 0.25 * 0.9,
            center - radius * 0.95 + radius * 0.25 * 0.9,
            center - radius * 0.95 + radius * 0.25 * 0.9
          ]}
          stroke={getStrokeColor()}
          strokeWidth={getStrokeStyles().strokeWidth}
        />
        </>
      );
    }

    if (shapeType === 'transgender-alt') {      const radius = size * 0.35;
      const center = size / 2;
      return (
        <>
          <Circle
            x={center}
            y={center}
            radius={radius}
            fill={getNodeColor()}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
          />
          <Line
            points={[
              center + radius * 0.7,
              center - radius * 0.7,
              center + radius * 1.4,
              center - radius * 1.4
            ]}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
          />
          <Line
            points={[
              center + radius * 1.4,
              center - radius * 1.4,
              center + radius * 1.0,
              center - radius * 1.4
            ]}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
          />
          <Line
            points={[
              center + radius * 1.4,
              center - radius * 1.4,
              center + radius * 1.4,
              center - radius * 1.0
            ]}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
          />
          <Line
          points={[
            center,
            center + radius * 1,
            center,
            center + radius * 1.6
          ]}
          stroke={getStrokeColor()}
          strokeWidth={getStrokeStyles().strokeWidth}
        />
        <Line
          points={[
            center - radius * 0.4,
            center + radius * 1.3,
            center + radius * 0.4,
            center + radius * 1.3
          ]}
          stroke={getStrokeColor()}
          strokeWidth={getStrokeStyles().strokeWidth}
        />
        <Line
        // Diagonal stem running northwest from circle
        points={[
          center - radius * 0.70,
          center - radius * 0.70,
          center - radius * 1.38,
          center - radius * 1.38
        ]}
        stroke={getStrokeColor()}
        strokeWidth={getStrokeStyles().strokeWidth}
      />
      <Line
        // Arrowhead leg on the north side of the stem tip
        points={[
          center - radius * 1.38,
          center - radius * 1.38,
          center - radius * 1.38 + radius * 0.35 * Math.SQRT1_2 + radius * 0.38 * 0.80 * Math.SQRT1_2,
          center - radius * 1.38 + radius * 0.35 * Math.SQRT1_2 - radius * 0.38 * 0.80 * Math.SQRT1_2
        ]}
        stroke={getStrokeColor()}
        strokeWidth={getStrokeStyles().strokeWidth}
      />
      <Line
        // Arrowhead leg on the west side of the stem tip
        points={[
          center - radius * 1.38,
          center - radius * 1.38,
          center - radius * 1.38 + radius * 0.35 * Math.SQRT1_2 - radius * 0.38 * 0.80 * Math.SQRT1_2,
          center - radius * 1.38 + radius * 0.35 * Math.SQRT1_2 + radius * 0.38 * 0.80 * Math.SQRT1_2
        ]}
        stroke={getStrokeColor()}
        strokeWidth={getStrokeStyles().strokeWidth}
      />
      <Line
        // Diagonal accent mirroring transgender star arm
        points={[
          center - radius * 0.95 - radius * 0.25,
          center - radius * 0.95 + radius * 0.25,
          center - radius * 0.95 + radius * 0.25,
          center - radius * 0.95 - radius * 0.25
        ]}
        stroke={getStrokeColor()}
        strokeWidth={getStrokeStyles().strokeWidth}
      />
        </>
      );
    }


    if (shapeType === 'genderqueer') {      const radius = size * 0.35;
      const center = size / 2;
      return (
        <>
          <Circle
            x={center}
            y={center}
            radius={radius}
            fill={getNodeColor()}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
          />
          <Line
            points={[
              center, center - radius,
              center, center - radius * 1.8
            ]}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
          />
          <Line
          // Star diagonal arm (NW–SE)
          points={[
            size / 2 - size * 0.35 * 0.30,
            size / 2 - size * 0.35 * 1.8 - size * 0.3 * 0.3,
            size / 2 + size * 0.35 * 0.30,
            size / 2 - size * 0.35 * 1.8 + size * 0.3 * 0.3
          ]}
          stroke={getStrokeColor()}
          strokeWidth={getStrokeStyles().strokeWidth}
        />
        <Line
          // Star diagonal arm (NE–SW)
          points={[
            size / 2 - size * 0.35 * 0.30,
            size / 2 - size * 0.35 * 1.8 + size * 0.3 * 0.3,
            size / 2 + size * 0.35 * 0.30,
            size / 2 - size * 0.35 * 1.8 - size * 0.3 * 0.3
          ]}
          stroke={getStrokeColor()}
          strokeWidth={getStrokeStyles().strokeWidth}
        />
        </>
      );
    }

    // Stillbirth - shape with X mark
    if (isStillbirth) {
      const baseShape = isMale ? (
        <Rect
          x={0}
          y={0}
          width={size}
          height={size}
          fill={getNodeColor()}
          stroke={getStrokeColor()}
          strokeWidth={getStrokeStyles().strokeWidth}
          dash={getStrokeStyles().dash}
          dashEnabled={getStrokeStyles().dashEnabled}
        />
      ) : (
        <Circle
          x={size/2}
          y={size/2}
          radius={size/2}
          fill={getNodeColor()}
          stroke={getStrokeColor()}
          strokeWidth={getStrokeStyles().strokeWidth}
          dash={getStrokeStyles().dash}
          dashEnabled={getStrokeStyles().dashEnabled}
        />
      );
      return (
        <>
          {baseShape}
          <Line
            points={[size * 0.15, size * 0.15, size * 0.85, size * 0.85]}
            strokeWidth={2}
            stroke={getStrokeColor()}
          />
          <Line
            points={[size * 0.85, size * 0.15, size * 0.15, size * 0.85]}
            strokeWidth={2}
            stroke={getStrokeColor()}
          />
        </>
      );
    }

    // Standard male - square
    if (isMale) {
      return (
        <>
          <Rect
            x={0}
            y={0}
            width={size}
            height={size}
            fill={getNodeColor()}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
            dash={getStrokeStyles().dash}
            dashEnabled={getStrokeStyles().dashEnabled}
          />
          {isDeceased && (
            <>
              <Line
                points={[-size * 0.1, -size * 0.1, size * 1.1, size * 1.1]}
                stroke={deceasedOverlayColor}
                strokeWidth={2}
              />
              <Line
                points={[size * 1.1, -size * 0.1, -size * 0.1, size * 1.1]}
                stroke={deceasedOverlayColor}
                strokeWidth={2}
              />
            </>
          )}
        </>
      );
    } else if (isFemale) {
      // Standard female - circle
      // Standard female - circle
      return (
        <>
          <Circle
            x={size/2}
            y={size/2}
            radius={size/2}
            fill={getNodeColor()}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
            dash={getStrokeStyles().dash}
            dashEnabled={getStrokeStyles().dashEnabled}
          />
          {isDeceased && (
            <>
              <Line
                points={[0, 0, size, size]}
                stroke={deceasedOverlayColor}
                strokeWidth={2}
              />
              <Line
                points={[0, size, size, 0]}
                stroke={deceasedOverlayColor}
                strokeWidth={2}
              />
            </>
          )}
        </>
      );
    } else {
      // Unknown gender - diamond top-left anchored
      return (
        <>
          <Line
            points={[
              size/2, 0,
              size, size/2,
              size/2, size,
              0, size/2
            ]}
            closed
            fill={getNodeColor()}
            stroke={getStrokeColor()}
            strokeWidth={getStrokeStyles().strokeWidth}
            dash={getStrokeStyles().dash}
            dashEnabled={getStrokeStyles().dashEnabled}
          />
          {isDeceased && (
            <>
              <Line
                points={[size*0.1, size*0.1, size*0.9, size*0.9]}
                stroke={deceasedOverlayColor}
                strokeWidth={2}
              />
              <Line
                points={[size*0.1, size*0.9, size*0.9, size*0.1]}
                stroke={deceasedOverlayColor}
                strokeWidth={2}
              />
            </>
          )}
        </>
      );
    }
  };
  
  const renderSplitFill = () => {
    if (node.attrs?.mixedHeritage) {
      const colors = node.attrs.heritageColors as string[] || ['#4682B4', '#90EE90'];
      if (node.sex === 'female') {
        return (
          <>
            <Circle
              x={width/2}
              y={height/2}
              radius={Math.min(width, height)/2}
              fill={colors[0]}
              stroke={getStrokeColor()}
              strokeWidth={selected ? 3 : 2}
            />
            <Circle
              x={width/2}
              y={height/2}
              radius={Math.min(width, height)/2}
              fill={colors[1]}
              stroke={getStrokeColor()}
              strokeWidth={selected ? 3 : 2}
              clipFunc={(ctx: any) => {
                ctx.rect(width/2, 0, width/2, height);
              }}
            />
          </>
        );
      }
    }
    return null;
  };

  // Subscribe only to the specific field to avoid rerenders on unrelated store changes
  const perspective = usePerspectiveStore(s => s.perspective);
  const healthViewMode = usePerspectiveStore(s => s.healthViewMode);
  const healthFocusCondition = usePerspectiveStore(s => s.healthFocusCondition);

  const cultureMix: ComputedCulture = cultureMixProp || [];
  const healthMix: ComputedHealth = healthMixProp || [];
  const normalizeTag = (value: string): string => value.trim().toLowerCase().replace(/\s+/g, '_');
  const matchesCondition = (tag: string, condition: string): boolean => {
    const left = normalizeTag(tag);
    const right = normalizeTag(condition);
    if (left === right) return true;
    return left.replace(/_/g, ' ') === right.replace(/_/g, ' ');
  };

  const getExplicitMedicalTags = (): string[] => {
    const attrs = ((node as any).attrs ?? {}) as Record<string, unknown>;
    const attrsList = Array.isArray(attrs.medicalConditionTags) ? attrs.medicalConditionTags : [];
    const topList = Array.isArray((node as any).medicalConditionTags) ? (node as any).medicalConditionTags : [];
    const primary = attrsList.length > 0 ? attrsList : topList;
    return (primary as unknown[])
      .map((entry: unknown) => String(entry ?? '').trim())
      .filter((entry: string) => entry.length > 0);
  };

  const renderCulturalPie = () => {
    if (perspective !== 'cultural' || cultureMix.length === 0) return null;
    const isFemale = node.sex === 'female';
    const isMale = node.sex === 'male';

    // Circle nodes → radial pie (arcs)
    if (isFemale) {
      let start = 0;
      return (
        <Group>
          {cultureMix.map((m) => {
            const angle = m.weight * 360;
            const arc = (
              <Arc key={`c-${m.tag}`} x={size/2} y={size/2} innerRadius={0} outerRadius={size/2} angle={angle} rotation={start} fill={m.color} />
            );
            start += angle;
            return arc;
          })}
        </Group>
      );
    }

    // Square/diamond nodes → shape-preserving stacked fill (no circular arcs)
    const clipFunc = (ctx: any) => {
      if (isMale) {
        // Rect clip for square
        ctx.rect(0, 0, size, size);
      } else {
        // Diamond clip for unknown gender
        ctx.moveTo(size/2, 0);
        ctx.lineTo(size, size/2);
        ctx.lineTo(size/2, size);
        ctx.lineTo(0, size/2);
        ctx.closePath();
      }
    };

    let y = 0;
    return (
      <Group clipFunc={clipFunc}>
        {cultureMix.map((m) => {
          const h = Math.max(1, m.weight * size);
          const rect = (
            <Rect key={`c-${m.tag}`} x={0} y={y} width={size} height={h} fill={m.color} />
          );
        
          y += h;
          return rect;
        })}
      </Group>
    );
  };

  const renderHealthRing = () => {
    if (perspective !== 'health' || healthMix.length === 0) return null;
    const anglePer = 360 / healthMix.length;
    let start = 0;
    return (
      <Group>
        {healthMix.map((m) => {
          const arc = (
            <Arc key={`h-${m.tag}`} x={size/2} y={size/2} innerRadius={size/2 - 6} outerRadius={size/2 - 2} angle={anglePer} rotation={start} fill={m.color} />
          );
          start += anglePer;
          return arc;
        })}
      </Group>
    );
  };

  const renderHealthEmojiBadges = () => {
    if (perspective !== 'health' || healthViewMode !== 'ring+emoji' || healthMix.length === 0) return null;

    const visible = healthMix.slice(0, 3);
    const extra = healthMix.length - visible.length;

    return (
      <Group x={size + 10} y={26}>
        {visible.map((item, idx) => {
          const y = idx * 18;
          return (
            <Group key={`hm-${item.tag}`} y={y}>
              <Circle
                x={0}
                y={0}
                radius={8}
                fill={item.color || '#6366f1'}
                stroke={item.color || '#6366f1'}
                strokeWidth={1.5}
              />
              <Text
                x={-6}
                y={-6}
                width={12}
                height={12}
                text={item.icon && item.icon.trim().length > 0 ? item.icon : '•'}
                align="center"
                verticalAlign="middle"
                fontSize={9}
                fill="#ffffff"
                listening={false}
              />
            </Group>
          );
        })}
        {extra > 0 ? (
          <Group y={visible.length * 18}>
            <Circle x={0} y={0} radius={8} fill="#e5e7eb" stroke="#9ca3af" strokeWidth={1.5} />
            <Text
              x={-6}
              y={-6}
              width={12}
              height={12}
              text={`+${extra}`}
              align="center"
              verticalAlign="middle"
              fontSize={8}
              fill="#374151"
              listening={false}
            />
          </Group>
        ) : null}
      </Group>
    );
  };

  const renderHealthFocusBadge = () => {
    if (perspective !== 'health') return null;
    if (!healthFocusCondition) return null;

    const focusItem = healthMix.find((item) => matchesCondition(item.tag, healthFocusCondition));
    if (!focusItem) return null;

    const explicitTags = getExplicitMedicalTags();
    const isDirect = explicitTags.some((tag) => matchesCondition(tag, healthFocusCondition));
    const badgeColor = focusItem.color || '#6366f1';
    const badgeFill = isDirect ? badgeColor : '#ffffff';
    const badgeStroke = badgeColor;
    const badgeText = focusItem.icon && focusItem.icon.trim().length > 0 ? focusItem.icon : (isDirect ? 'D' : 'R');

    return (
      <Group x={size + 10} y={8}>
        <Circle
          x={0}
          y={0}
          radius={9}
          fill={badgeFill}
          stroke={badgeStroke}
          strokeWidth={2}
          dash={isDirect ? undefined : [3, 2]}
          dashEnabled={!isDirect}
        />
        <Text
          x={-6}
          y={-6}
          width={12}
          height={12}
          text={badgeText}
          align="center"
          verticalAlign="middle"
          fontSize={10}
          fill={isDirect ? '#ffffff' : badgeStroke}
          listening={false}
        />
      </Group>
    );
  };

  useEffect(() => {
    if (!isPending) return;
    const border = pendingBorderRef.current;
    if (!border) return;
    const layer = border.getLayer();
    if (!layer) return;

    const animation = new Konva.Animation((frame) => {
      if (!frame) return;
      const hue = (frame.time / 12) % 360;
      const stroke = `hsl(${hue}, 85%, 62%)`;
      border.stroke(stroke);
      border.shadowColor(stroke);
    }, layer);

    animation.start();
    return () => {
      animation.stop();
    };
  }, [isPending]);

  // Center text labels dynamically based on their natural width
  useLayoutEffect(() => {
    const nameText = nameTextRef.current;
    if (nameText) {
      const textWidth = nameText.width();
      const rawAlign = (((node.attrs as any) || {}).textAlign || 'center') as string;
      const effectiveAlign = rawAlign === 'justify' ? 'left' : rawAlign;
      if (effectiveAlign === 'right') {
        nameText.offsetX(textWidth);
      } else if (effectiveAlign === 'center') {
        nameText.offsetX(textWidth / 2);
      } else {
        nameText.offsetX(0);
      }
    }
    const locationText = locationTextRef.current;
    if (locationText) {
      const textWidth = locationText.width();
      const rawAlign = (((node.attrs as any) || {}).textAlign || 'center') as string;
      const effectiveAlign = rawAlign === 'justify' ? 'left' : rawAlign;
      if (effectiveAlign === 'right') {
        locationText.offsetX(textWidth);
      } else if (effectiveAlign === 'center') {
        locationText.offsetX(textWidth / 2);
      } else {
        locationText.offsetX(0);
      }
    }
  }, [node.name, node.location, secondLine, (node.attrs as any)?.textAlign, getTextStyles]);

  if (isPending) {
    const placeholderWidth = resolveEffectiveShapeDimension(node, 'width') ?? 280;
    const placeholderHeight = resolveEffectiveShapeDimension(node, 'height') ?? 180;
    const title = (node.attrs as any)?.pendingTitle || 'Generating new layout…';
    const subtitle = (node.attrs as any)?.pendingSubtitle || 'Give us a moment while we prepare the draft.';

    return (
      <Group
        id={node.id}
        name="shape"
        ref={ref}
        x={node.x}
        y={node.y}
        width={placeholderWidth}
        height={placeholderHeight}
        listening={false}
      >
        <Rect
          x={0}
          y={0}
          width={placeholderWidth}
          height={placeholderHeight}
          cornerRadius={24}
          fill="rgba(15, 23, 42, 0.08)"
          shadowColor="rgba(30, 64, 175, 0.25)"
          shadowBlur={24}
          shadowOpacity={0.35}
        />
        <Rect
          ref={pendingBorderRef}
          x={4}
          y={4}
          width={placeholderWidth - 8}
          height={placeholderHeight - 8}
          cornerRadius={20}
          stroke="#60A5FA"
          strokeWidth={4}
          dash={[16, 12]}
        />
        <Text
          text={title}
          width={placeholderWidth}
          align="center"
          fontSize={18}
          fontStyle="bold"
          fill="#0F172A"
          y={placeholderHeight / 2 - 24}
        />
        <Text
          text={subtitle}
          width={placeholderWidth - 48}
          x={24}
          align="center"
          wrap="word"
          fontSize={13}
          fill="#1E293B"
          y={placeholderHeight / 2 + 4}
        />
      </Group>
    );
  }

  return (
    <Group
      id={node.id}
      name="shape"
      ref={ref}
      x={node.x}
      y={node.y}
      width={size}
      height={size}
      draggable={canWrite && !editingField}
      perfectDrawEnabled={false}
      onDragStart={(e) => {
        if (onDragStart) {
          onDragStart({ x: e.target.x(), y: e.target.y() });
        }
      }}
      onDragMove={(e) => {
        onDragMove({ x: e.target.x(), y: e.target.y() });
      }}
      onDragEnd={(e) => {
        const gx = Math.round(e.target.x() / snapToGrid) * snapToGrid;
        const gy = Math.round(e.target.y() / snapToGrid) * snapToGrid;
        e.target.position({ x: gx, y: gy });
        onDragEnd({ x: gx, y: gy });
      }}
      onClick={(e) => {
        if (!editingField) {
          onClick(e);
        }
      }}
      onTap={(e) => {
        if (!editingField) {
          onClick(e);
        }
      }}
      onTransformStart={onTransformStart}
      onTransformEnd={onTransformEnd}
    >
      <Group
        name="rotation-group"
        x={size/2}
        y={size/2}
        offsetX={size/2}
        offsetY={size/2}
        rotation={rotation}
        onTransformStart={onTransformStart}
        onTransformEnd={onTransformEnd}
      >
        <Group name="shape-content" onTransform={onTransform} ref={(n: any) => {
          // Expose shape-only ref for accurate connector sizing
          try {
            if (n) {
              (n as any)._isShapeContent = true;
            }
          } catch {}
        }}>
          {renderSplitFill() || renderShape()}
      {renderCulturalPie()}
      {renderHealthRing()}
      {renderHealthEmojiBadges()}
      {renderHealthFocusBadge()}
        </Group>
      </Group>

      {/* Rotating label group that is NOT part of transform bounds */}
      <Group
        x={size/2}
        y={size/2}
        offsetX={size/2}
        offsetY={size/2}
        rotation={rotation}
      >
        {/* Name text - no truncation, centered below shape, dynamic width */}
        <Group>
          <Text
            ref={nameTextRef}
            text={displayPersonName}
            x={(() => {
              const rawAlign = (((node.attrs as any) || {}).textAlign || 'center') as string;
              const effectiveAlign = rawAlign === 'right' ? 'right' : rawAlign === 'center' ? 'center' : 'left';
              if (effectiveAlign === 'right') return size;
              if (effectiveAlign === 'center') return size / 2;
              return 0;
            })()}
            y={size + 5}
            align={(() => {
              const rawAlign = (((node.attrs as any) || {}).textAlign || 'center') as string;
              return (rawAlign === 'right' ? 'right' : rawAlign === 'center' ? 'center' : 'left') as any;
            })()}
            fontSize={getTextStyles().fontSize}
            fontFamily={getTextStyles().fontFamily}
            fontStyle={getTextStyles().fontStyle}
            fontWeight={getTextStyles().fontWeight}
            textDecoration={getTextStyles().textDecoration}
            fill={getTextStyles().textColor}
            visible={editingField !== 'name'}
            listening={true}
            hitStrokeWidth={10}
            onDblClick={handleNameDblClick}
            onDblTap={handleNameDblClick}
          />
        </Group>
        {secondLine && (
          <Group>
            <Text
              ref={locationTextRef}
              text={secondLine as string}
              x={(() => {
                const rawAlign = (((node.attrs as any) || {}).textAlign || 'center') as string;
                const effectiveAlign = rawAlign === 'right' ? 'right' : rawAlign === 'center' ? 'center' : 'left';
                if (effectiveAlign === 'right') return size;
                if (effectiveAlign === 'center') return size / 2;
                return 0;
              })()}
              y={secondLineTopY}
              align={(() => {
                const rawAlign = (((node.attrs as any) || {}).textAlign || 'center') as string;
                return (rawAlign === 'right' ? 'right' : rawAlign === 'center' ? 'center' : 'left') as any;
              })()}
              fontSize={10}
              fontFamily="system-ui, -apple-system, sans-serif"
              fontStyle="normal"
              fontWeight="normal"
              textDecoration="none"
              fill={getThemeColor('#666666', { type: 'foreground' })}
              visible={true}
              listening={false}
            />
          </Group>
        )}
      </Group>

    </Group>
  );
});

PersonNodeInner.displayName = 'PersonNode';

const areEqual = (prev: PersonNodeProps, next: PersonNodeProps) => {
  // Check if node, selected, or editing field changed
  if (prev.node !== next.node || prev.selected !== next.selected || prev.editingField !== next.editingField) {
    return false;
  }

  // Check if cultureMix changed (needed for cultural perspective rendering)
  const prevCulture = prev.cultureMix || [];
  const nextCulture = next.cultureMix || [];
  if (prevCulture.length !== nextCulture.length) return false;
  for (let i = 0; i < prevCulture.length; i++) {
    if (prevCulture[i].tag !== nextCulture[i].tag || prevCulture[i].weight !== nextCulture[i].weight || prevCulture[i].color !== nextCulture[i].color) {
      return false;
    }
  }

  // Check if healthMix changed (needed for health perspective rendering)
  const prevHealth = prev.healthMix || [];
  const nextHealth = next.healthMix || [];
  if (prevHealth.length !== nextHealth.length) return false;
  for (let i = 0; i < prevHealth.length; i++) {
    if (prevHealth[i].tag !== nextHealth[i].tag || prevHealth[i].color !== nextHealth[i].color || prevHealth[i].icon !== nextHealth[i].icon) {
      return false;
    }
  }

  // Props are equal, skip re-render
  return true;
};

export const PersonNode = memo(PersonNodeInner, areEqual);
